// src/hooks/workspace/useFocusTracking.ts
import { useEffect, useRef } from "react";
import { useWorkspaceStore, type FocusType } from "@/stores/workspaceStore";

interface UseFocusTrackingOptions {
  /** The type of entity being focused (token, sector, etc.) */
  type: FocusType;
  /** The entity identifier (e.g., "SOL", "layer1") */
  entityId: string | null | undefined;
  /** Human-readable name for the entity (e.g., "Solana", "Layer 1") */
  entityName?: string | undefined;
  /** Whether focus tracking is enabled (default: true) */
  enabled?: boolean;
  /** Whether to clear focus when component unmounts (default: false) */
  clearOnUnmount?: boolean;
}

interface PrevFocusRef {
  type: FocusType;
  entityId: string | null | undefined;
  entityName: string | undefined;
}

/**
 * Hook to automatically update workspace focus when a page/component mounts.
 * Use this in token dashboards, sector pages, etc. to track what the user is viewing.
 *
 * @example
 * // In TokenDashboardV2.tsx
 * useFocusTracking({
 *   type: 'token',
 *   entityId: tokenId,
 *   entityName: tokenInfo?.name,
 * });
 *
 * @example
 * // In SectorDashboardV2.tsx
 * useFocusTracking({
 *   type: 'sector',
 *   entityId: dashboardType, // e.g., 'layer1'
 *   entityName: sectorDisplayName, // e.g., 'Layer 1'
 * });
 */
export function useFocusTracking({
  type,
  entityId,
  entityName,
  enabled = true,
  clearOnUnmount = false,
}: UseFocusTrackingOptions): void {
  const setFocus = useWorkspaceStore((s) => s.setFocus);
  const clearFocus = useWorkspaceStore((s) => s.clearFocus);

  // Track previous values to avoid unnecessary updates
  const prevRef = useRef<PrevFocusRef>({
    type: null,
    entityId: null,
    entityName: undefined,
  });

  useEffect(() => {
    if (!enabled) return;

    // Check if values have actually changed
    const prev = prevRef.current;
    const hasChanged =
      prev.type !== type ||
      prev.entityId !== entityId ||
      prev.entityName !== entityName;

    if (!hasChanged) return;

    // Update the ref
    prevRef.current = { type, entityId, entityName: entityName };

    // Set focus if we have valid values
    if (type && entityId) {
      setFocus(type, entityId, entityName);
    }
  }, [type, entityId, entityName, enabled, setFocus]);

  // Optionally clear focus on unmount
  useEffect(() => {
    if (!clearOnUnmount) return;

    return () => {
      clearFocus();
    };
  }, [clearOnUnmount, clearFocus]);
}

/**
 * Hook for comparison views where multiple entities are being viewed together.
 *
 * @example
 * // In TokenComparisonPage.tsx
 * useComparisonFocusTracking({
 *   type: 'token',
 *   entities: ['SOL', 'ETH', 'AVAX'],
 * });
 */
export function useComparisonFocusTracking({
  type,
  entities,
  label,
  enabled = true,
}: {
  type: FocusType;
  entities: string[];
  label?: string;
  enabled?: boolean;
}): void {
  const setComparisonFocus = useWorkspaceStore((s) => s.setComparisonFocus);

  // Track previous values
  const prevEntitiesRef = useRef<string[]>([]);

  useEffect(() => {
    if (!enabled || !type || entities.length === 0) return;

    // Check if entities have changed
    const prevEntities = prevEntitiesRef.current;
    const entitiesChanged =
      prevEntities.length !== entities.length ||
      entities.some((e, i) => prevEntities[i] !== e);

    if (!entitiesChanged) return;

    prevEntitiesRef.current = entities;
    setComparisonFocus(type, entities, label);
  }, [type, entities, label, enabled, setComparisonFocus]);
}
